import { createSlice } from '@reduxjs/toolkit';

import {
  apiGetGroup,
  apiGetGroups,
  apiGetGroupCategories,
  apiGetSuggestedGroups,
  apiCreateGroup,
  apiUpdateGroup,
  apiJoinGroup,
  apiLeaveGroup,
  apiMuteGroup,
  apiUnmuteGroup,
  apiRemoveGroupMember,
  apiTransferGroupOwnership,
  apiUpdateGroupMemberRole,
  apiBanGroupMember,
  apiUnbanGroupAccount,
  apiAuthorizeGroupMembershipRequest,
  apiRejectGroupMembershipRequest,
  apiAcceptGroupInvite,
  apiRejectGroupInvite,
} from '@/mastodon/api/groups';
import type {
  ApiGroupJSON,
  ApiGroupCategoryJSON,
  ApiGroupMembershipRole,
  ApiCreateGroupPayload,
  ApiUpdateGroupPayload,
} from '@/mastodon/api_types/groups';
import { createAppAsyncThunk } from '@/mastodon/store/typed_functions';

type QueryStatus = 'idle' | 'loading' | 'error';

interface GroupIdsQuery {
  ids: string[];
  status: QueryStatus;
}

interface GroupsState {
  groups: Record<string, ApiGroupJSON>;
  categories: ApiGroupCategoryJSON[];
  byCategoryId: Record<string, GroupIdsQuery>;
  myGroups: GroupIdsQuery;
  administeredGroups: GroupIdsQuery;
  suggestedGroups: GroupIdsQuery;
}

const initialGroupsState: GroupsState = {
  groups: {},
  categories: [],
  byCategoryId: {},
  myGroups: { ids: [], status: 'idle' },
  administeredGroups: { ids: [], status: 'idle' },
  suggestedGroups: { ids: [], status: 'idle' },
};

export const fetchGroupCategories = createAppAsyncThunk(
  'groups/fetchCategories',
  () => apiGetGroupCategories(),
);

export const fetchGroupsByCategory = createAppAsyncThunk(
  'groups/fetchByCategory',
  (categoryId: string) => apiGetGroups({ category_id: categoryId }),
);

export const fetchMyGroups = createAppAsyncThunk('groups/fetchMine', () =>
  apiGetGroups({ membership: 'member' }),
);

export const fetchAdministeredGroups = createAppAsyncThunk(
  'groups/fetchAdministered',
  () => apiGetGroups({ membership: 'admin' }),
);

export const fetchSuggestedGroups = createAppAsyncThunk(
  'groups/fetchSuggested',
  () => apiGetSuggestedGroups(),
);

export const fetchGroup = createAppAsyncThunk('groups/fetchOne', (id: string) =>
  apiGetGroup(id),
);

export const createGroup = createAppAsyncThunk(
  'groups/create',
  (payload: ApiCreateGroupPayload) => apiCreateGroup(payload),
);

export const updateGroup = createAppAsyncThunk(
  'groups/update',
  (payload: ApiUpdateGroupPayload) => apiUpdateGroup(payload),
);

export const joinGroup = createAppAsyncThunk(
  'groups/join',
  async (groupId: string) => {
    const result = await apiJoinGroup(groupId);
    // A membership has a `role`; a pending request for a private group does not.
    const pending = !('role' in result);
    return { groupId, pending };
  },
);

export const leaveGroup = createAppAsyncThunk(
  'groups/leave',
  async (groupId: string) => {
    await apiLeaveGroup(groupId);
    return groupId;
  },
);

export const muteGroup = createAppAsyncThunk(
  'groups/mute',
  async (groupId: string) => {
    await apiMuteGroup(groupId);
    return groupId;
  },
);

export const unmuteGroup = createAppAsyncThunk(
  'groups/unmute',
  async (groupId: string) => {
    await apiUnmuteGroup(groupId);
    return groupId;
  },
);

export const transferGroupOwnership = createAppAsyncThunk(
  'groups/transferOwnership',
  ({
    groupId,
    newOwnerAccountId,
  }: {
    groupId: string;
    newOwnerAccountId: string;
  }) => apiTransferGroupOwnership(groupId, newOwnerAccountId),
);

export const removeGroupMember = createAppAsyncThunk(
  'groups/removeMember',
  async ({ groupId, accountId }: { groupId: string; accountId: string }) => {
    await apiRemoveGroupMember(groupId, accountId);
    return groupId;
  },
);

export const updateGroupMemberRole = createAppAsyncThunk(
  'groups/updateMemberRole',
  ({
    groupId,
    accountId,
    role,
  }: {
    groupId: string;
    accountId: string;
    role: ApiGroupMembershipRole;
  }) => apiUpdateGroupMemberRole(groupId, accountId, role),
);

export const banGroupMember = createAppAsyncThunk(
  'groups/banMember',
  async ({
    groupId,
    accountId,
    reason,
  }: {
    groupId: string;
    accountId: string;
    reason?: string;
  }) => {
    await apiBanGroupMember(groupId, accountId, reason);
    return groupId;
  },
);

export const unbanGroupAccount = createAppAsyncThunk(
  'groups/unbanAccount',
  async ({ groupId, accountId }: { groupId: string; accountId: string }) => {
    await apiUnbanGroupAccount(groupId, accountId);
    return groupId;
  },
);

export const authorizeGroupMembershipRequest = createAppAsyncThunk(
  'groups/authorizeMembershipRequest',
  async ({ groupId, requestId }: { groupId: string; requestId: string }) => {
    await apiAuthorizeGroupMembershipRequest(groupId, requestId);
    return groupId;
  },
);

export const rejectGroupMembershipRequest = createAppAsyncThunk(
  'groups/rejectMembershipRequest',
  async ({ groupId, requestId }: { groupId: string; requestId: string }) => {
    await apiRejectGroupMembershipRequest(groupId, requestId);
    return groupId;
  },
);

export const acceptGroupInvite = createAppAsyncThunk(
  'groups/acceptInvite',
  (inviteId: string) => apiAcceptGroupInvite(inviteId),
);

export const rejectGroupInvite = createAppAsyncThunk(
  'groups/rejectInvite',
  async (inviteId: string) => {
    await apiRejectGroupInvite(inviteId);
    return inviteId;
  },
);

const importGroups = (
  state: GroupsState,
  groups: ApiGroupJSON[] | ApiGroupJSON,
) => {
  const list = Array.isArray(groups) ? groups : [groups];
  list.forEach((group) => {
    state.groups[group.id] = group;
  });
};

const groupsSlice = createSlice({
  name: 'groups',
  initialState: initialGroupsState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchGroupCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(fetchGroupsByCategory.pending, (state, action) => {
        state.byCategoryId[action.meta.arg] = { ids: [], status: 'loading' };
      })
      .addCase(fetchGroupsByCategory.fulfilled, (state, action) => {
        importGroups(state, action.payload);
        state.byCategoryId[action.meta.arg] = {
          ids: action.payload.map((group) => group.id),
          status: 'idle',
        };
      })
      .addCase(fetchGroupsByCategory.rejected, (state, action) => {
        state.byCategoryId[action.meta.arg] = { ids: [], status: 'error' };
      })
      .addCase(fetchMyGroups.pending, (state) => {
        state.myGroups.status = 'loading';
      })
      .addCase(fetchMyGroups.fulfilled, (state, action) => {
        importGroups(state, action.payload);
        state.myGroups = {
          ids: action.payload.map((group) => group.id),
          status: 'idle',
        };
      })
      .addCase(fetchMyGroups.rejected, (state) => {
        state.myGroups.status = 'error';
      })
      .addCase(fetchAdministeredGroups.pending, (state) => {
        state.administeredGroups.status = 'loading';
      })
      .addCase(fetchAdministeredGroups.fulfilled, (state, action) => {
        importGroups(state, action.payload);
        state.administeredGroups = {
          ids: action.payload.map((group) => group.id),
          status: 'idle',
        };
      })
      .addCase(fetchAdministeredGroups.rejected, (state) => {
        state.administeredGroups.status = 'error';
      })
      .addCase(fetchSuggestedGroups.pending, (state) => {
        state.suggestedGroups.status = 'loading';
      })
      .addCase(fetchSuggestedGroups.fulfilled, (state, action) => {
        importGroups(state, action.payload);
        state.suggestedGroups = {
          ids: action.payload.map((group) => group.id),
          status: 'idle',
        };
      })
      .addCase(fetchSuggestedGroups.rejected, (state) => {
        state.suggestedGroups.status = 'error';
      })
      .addCase(fetchGroup.fulfilled, (state, action) => {
        importGroups(state, action.payload);
      })
      .addCase(createGroup.fulfilled, (state, action) => {
        importGroups(state, action.payload);
      })
      .addCase(updateGroup.fulfilled, (state, action) => {
        importGroups(state, action.payload);
      })
      .addCase(joinGroup.fulfilled, (state, action) => {
        const { groupId, pending } = action.payload;
        const group = state.groups[groupId];
        if (!group) {
          return;
        }
        if (pending) {
          group.pending_membership_request = true;
        } else {
          group.member = true;
          group.role = 'member';
          group.members_count += 1;
        }
      })
      .addCase(leaveGroup.fulfilled, (state, action) => {
        const group = state.groups[action.payload];
        if (group) {
          group.member = false;
          group.role = null;
          group.members_count = Math.max(0, group.members_count - 1);
        }
      })
      .addCase(muteGroup.fulfilled, (state, action) => {
        const group = state.groups[action.payload];
        if (group) {
          group.muted = true;
        }
      })
      .addCase(unmuteGroup.fulfilled, (state, action) => {
        const group = state.groups[action.payload];
        if (group) {
          group.muted = false;
        }
      })
      .addCase(transferGroupOwnership.fulfilled, (state, action) => {
        importGroups(state, action.payload);
      })
      .addCase(removeGroupMember.fulfilled, (state, action) => {
        const group = state.groups[action.payload];
        if (group) {
          group.members_count = Math.max(0, group.members_count - 1);
        }
      })
      .addCase(banGroupMember.fulfilled, (state, action) => {
        const group = state.groups[action.payload];
        if (group) {
          group.members_count = Math.max(0, group.members_count - 1);
        }
      })
      .addCase(authorizeGroupMembershipRequest.fulfilled, (state, action) => {
        const group = state.groups[action.payload];
        if (group) {
          group.members_count += 1;
        }
      })
      .addCase(acceptGroupInvite.fulfilled, (state, action) => {
        const { group_id: groupId } = action.payload;
        const group = state.groups[groupId];
        if (group) {
          group.member = true;
          group.role = 'member';
          group.members_count += 1;
        }
        state.suggestedGroups.ids = state.suggestedGroups.ids.filter(
          (id) => id !== groupId,
        );
      });
  },
});

export const groups = groupsSlice.reducer;
